import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ShoppingCart } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/site";
import { useLeadModal } from "./LeadModalContext";
import { useLanguage, LanguageMenu } from "../lib/language";
import { useCart } from "../store/CartContext";

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="header-logo" className="flex min-w-0 items-center gap-2.5" aria-label="Drive With Confidence — Home">
    <img
      src="/dwc-logo.jpeg"
      alt="Drive With Confidence logo"
      className="h-10 w-10 max-w-none shrink-0 rounded-full object-cover shadow-[0_8px_20px_rgba(0,0,0,0.15)] ring-1 ring-black/5 sm:h-11 sm:w-11"
    />
    <span className="min-w-0 xl:hidden">
      <span className={`block max-w-[9rem] text-[12px] font-extrabold leading-[1.15] tracking-tight sm:max-w-none sm:text-[13px] ${dark ? "text-white" : "text-dwc-charcoal"}`}>
        Drive With Confidence
      </span>
    </span>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { open } = useLeadModal();
  const { t } = useLanguage();
  const { count } = useCart();
  const location = useLocation();
  const inStore = location.pathname.startsWith("/store");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled ? "border-b border-gray-100 bg-white/95 shadow-[0_10px_40px_rgba(0,0,0,0.06)] backdrop-blur-md" : "bg-white/60 backdrop-blur-md"
        }`}
      >
        <div data-testid="header-guarantee-badge" className="bg-dwc-green py-1.5 text-center text-[10px] font-extrabold uppercase tracking-[0.22em] text-white">
          <span className="mx-2 inline-block h-1.5 w-1.5 rounded-full bg-dwc-crimson align-middle" aria-hidden="true" />
          100% Money-Back Driving Guarantee
          <span className="mx-2 hidden text-emerald-200 sm:inline">· Where Psychology Meets The Road</span>
        </div>
        <div className="mx-auto flex h-[68px] max-w-[1440px] items-center gap-3 px-4 sm:px-5 lg:px-8">
          <div className="flex min-w-0 flex-1 items-center xl:min-w-fit xl:flex-none">
            <Logo />
          </div>
          <div className="hidden shrink-0 xl:block">
            <LanguageMenu />
          </div>
          <nav className="hidden flex-1 items-center justify-center gap-0.5 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-2.5 py-2 text-[12.5px] font-semibold tracking-wide transition-colors duration-200 ${
                    isActive ? "bg-dwc-soft text-dwc-green" : "text-dwc-charcoal/70 hover:bg-dwc-soft hover:text-dwc-green"
                  }`
                }
              >
                {t.nav[link.label] || link.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link
              to="/store"
              data-testid="header-store-btn"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-xl border border-dwc-crimson/30 px-3.5 py-2.5 text-[12px] font-extrabold uppercase tracking-wide text-dwc-crimson transition-colors hover:bg-dwc-crimson/[0.05] xl:inline-flex"
            >
              <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
              DWC Store
            </Link>
            <Link to={inStore ? "/store/cart" : "/store"} data-testid="header-cart-btn" aria-label={inStore ? `Cart, ${count} items` : "DWC Store"} className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-dwc-green/30 text-dwc-green transition-colors hover:bg-dwc-soft">
              <span className="absolute inset-0 animate-ping rounded-xl border-2 border-dwc-green/30 [animation-duration:3s]" aria-hidden="true" />
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              {count > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-dwc-crimson px-1 text-[10px] font-black text-white" data-testid="header-cart-count">
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => open()}
              data-testid="header-book-assessment-btn"
              className="hidden whitespace-nowrap rounded-xl bg-dwc-green px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(23,107,58,0.28)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-dwc-green-dark xl:inline-flex"
            >
              {t.bookShort}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-open-btn"
              aria-label="Open menu"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-dwc-charcoal xl:hidden"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[80] bg-dwc-charcoal/50 backdrop-blur-sm xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-white p-6 shadow-2xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between">
                <Logo />
                <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close-btn" aria-label="Close menu" className="rounded-full border border-gray-200 p-2">
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile">
                <div className="mb-2">
                  <LanguageMenu full />
                </div>
                {NAV_LINKS.map((link, i) => (
                  <motion.div key={link.to} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}>
                    <NavLink
                      to={link.to}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `block rounded-xl px-4 py-3 text-base font-bold ${isActive ? "bg-dwc-soft text-dwc-green" : "text-dwc-charcoal/80 hover:bg-dwc-soft"}`
                      }
                    >
                      {t.nav[link.label] || link.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <Link
                to="/store"
                data-testid="mobile-nav-dwc-store"
                className="mt-2 flex items-center gap-2 rounded-xl border border-dwc-crimson/25 bg-dwc-crimson/[0.04] px-4 py-3 text-base font-extrabold text-dwc-crimson"
              >
                <ShoppingCart className="h-4 w-4" aria-hidden="true" /> DWC Store
              </Link>
              <div className="mt-auto space-y-3 pb-20">
                <button
                  onClick={() => { setMenuOpen(false); open(); }}
                  data-testid="mobile-menu-book-btn"
                  className="w-full rounded-xl bg-dwc-green px-5 py-4 text-sm font-bold uppercase tracking-wide text-white"
                >
                  {t.bookShort}
                </button>
                <a href={SITE.phoneHref} data-testid="mobile-menu-call-btn" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dwc-green px-5 py-4 text-sm font-bold text-dwc-green">
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
